import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { createRoute, PUBLIC_PATH } from '@core/router/paths';

import { useHasPermission } from '@shared/hooks/use-has-permission';

import type { TEntity } from '@shared/constants/entities';
import type { Mechanism, PermissionAction } from '../types';

type HasPermissionProps = {
  children: ReactNode | ((isAllowed: boolean) => ReactNode);
  entity?: TEntity | TEntity[];
  action: PermissionAction | PermissionAction[];
  mechanism?: Mechanism;
  fallback?: ReactNode;
};

export function HasPermission({
  children,
  entity,
  action,
  mechanism = 'link',
  fallback = null,
}: HasPermissionProps) {
  const { pathname } = useLocation();
  const checkPermission = useHasPermission();

  const isAllowed = entity ? checkPermission(entity, action) : true;

  if (typeof children === 'function') {
    return <>{children(isAllowed)}</>;
  }

  if (!isAllowed) {
    if (mechanism === 'link') return null;
    if (mechanism === 'route') {
      return (
        fallback || (
          <Navigate
            to={`${createRoute(PUBLIC_PATH.UNAUTHORIZED)}?redirect=${pathname}`}
            state={{ codeName: `${action} ${entity}` }}
            replace
          />
        )
      );
    }
  }

  return <>{children}</>;
}
